import React, { useState, useEffect } from 'react';
import { Bot, Wand2, Settings, Code, Database, Zap, Plus, Trash2, Save, Play, Upload, Link, Brain, PenTool as Tool, FileText, Globe, Key, CheckCircle, AlertCircle, Loader, X } from 'lucide-react';
import { useAuth } from '../lib/AuthContext';

interface Tool {
  id: string;
  name: string;
  description: string;
  schema: any;
  endpoint?: string;
  type: 'api' | 'function' | 'database' | 'web_search';
}

interface KnowledgeBase {
  id: string;
  name: string;
  type: 'documents' | 'url' | 'database' | 'vector_store';
  source: string;
  status: 'pending' | 'processing' | 'ready' | 'error';
}

interface AgentConfig {
  name: string;
  description: string;
  personality: string;
  instructions: string;
  llmProvider: string;
  model: string;
  temperature: number;
  maxTokens: number;
  tools: Tool[];
  knowledgeBases: KnowledgeBase[];
  a2aEnabled: boolean;
  capabilities: string[];
  memoryEnabled: boolean;
  price: number;
  pricingModel: 'free' | 'one-time' | 'subscription_eligible';
}

interface RealAgentBuilderProps {
  isOpen: boolean;
  onClose: () => void;
}

const LLM_PROVIDERS = [
  { id: 'openai', name: 'OpenAI', models: ['gpt-4', 'gpt-3.5-turbo'] },
  { id: 'anthropic', name: 'Anthropic', models: ['claude-3-sonnet-20240229', 'claude-3-haiku-20240307'] },
  { id: 'groq', name: 'Groq', models: ['llama3-8b-8192', 'mixtral-8x7b-32768'] },
  { id: 'together', name: 'Together.ai', models: ['meta-llama/Llama-2-7b-chat-hf', 'mistralai/Mixtral-8x7B-Instruct-v0.1'] },
  { id: 'huggingface', name: 'Hugging Face', models: ['mistralai/Mistral-7B-Instruct-v0.2', 'microsoft/DialoGPT-medium'] }
];

const PREDEFINED_TOOLS = [
  {
    id: 'web_search',
    name: 'Web Search',
    description: 'Search the web for current information',
    type: 'api',
    schema: {
      type: 'function',
      function: {
        name: 'search_web',
        description: 'Search the web for information',
        parameters: {
          type: 'object',
          properties: {
            query: { type: 'string', description: 'Search query' },
            num_results: { type: 'number', description: 'Number of results to return', default: 5 }
          },
          required: ['query']
        }
      }
    }
  },
  {
    id: 'calculator',
    name: 'Calculator',
    description: 'Perform mathematical calculations',
    type: 'function',
    schema: {
      type: 'function',
      function: {
        name: 'calculate',
        description: 'Perform mathematical calculations',
        parameters: {
          type: 'object',
          properties: {
            expression: { type: 'string', description: 'Mathematical expression to evaluate' }
          },
          required: ['expression']
        }
      }
    }
  },
  {
    id: 'weather',
    name: 'Weather API',
    description: 'Get current weather information',
    type: 'api',
    schema: {
      type: 'function',
      function: {
        name: 'get_weather',
        description: 'Get current weather for a location',
        parameters: {
          type: 'object',
          properties: {
            location: { type: 'string', description: 'City name or coordinates' },
            units: { type: 'string', enum: ['metric', 'imperial'], default: 'metric' }
          },
          required: ['location']
        }
      }
    }
  },
  {
    id: 'email',
    name: 'Email Sender',
    description: 'Send emails via API',
    type: 'api',
    schema: {
      type: 'function',
      function: {
        name: 'send_email',
        description: 'Send an email',
        parameters: {
          type: 'object',
          properties: {
            to: { type: 'string', description: 'Recipient email address' },
            subject: { type: 'string', description: 'Email subject' },
            body: { type: 'string', description: 'Email body content' }
          },
          required: ['to', 'subject', 'body']
        }
      }
    }
  }
];

export function RealAgentBuilder({ isOpen, onClose }: RealAgentBuilderProps) {
  const [step, setStep] = useState<'config' | 'tools' | 'knowledge' | 'test' | 'deploy'>('config');
  const [agentConfig, setAgentConfig] = useState<AgentConfig>({
    name: '',
    description: '',
    personality: 'You are a helpful AI assistant.',
    instructions: '',
    llmProvider: 'groq',
    model: 'llama3-8b-8192',
    temperature: 0.7,
    maxTokens: 1024,
    tools: [],
    knowledgeBases: [],
    a2aEnabled: true,
    capabilities: [],
    memoryEnabled: true,
    price: 0,
    pricingModel: 'free'
  });
  const [selectedTools, setSelectedTools] = useState<Tool[]>([]);
  const [customTool, setCustomTool] = useState<Partial<Tool>>({});
  const [showCustomTool, setShowCustomTool] = useState(false);
  const [testMessage, setTestMessage] = useState('');
  const [testResponse, setTestResponse] = useState('');
  const [testing, setTesting] = useState(false);
  const [building, setBuilding] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const { user } = useAuth();

  // Memory system configuration
  const [memorySystemConfig, setMemorySystemConfig] = useState({
    enabled: true,
    type: 'hybrid' as 'conversational' | 'factual' | 'episodic' | 'semantic' | 'hybrid',
    retention_days: 30,
    max_conversations: 100,
    max_facts: 1000,
    learning_rate: 0.1,
    auto_cleanup: true
  });

  const handleAddTool = (tool: Tool) => {
    if (!selectedTools.find(t => t.id === tool.id)) {
      setSelectedTools([...selectedTools, tool]);
    }
  };

  const handleRemoveTool = (toolId: string) => {
    setSelectedTools(selectedTools.filter(t => t.id !== toolId));
  };

  const handleAddCustomTool = () => {
    if (!customTool.name || !customTool.description) {
      setError('Tool name and description are required');
      return;
    }

    const newTool: Tool = {
      id: `custom_${Date.now()}`,
      name: customTool.name,
      description: customTool.description,
      type: customTool.type || 'function',
      schema: customTool.schema || {
        type: 'function',
        function: {
          name: customTool.name.toLowerCase().replace(/\s+/g, '_'),
          description: customTool.description,
          parameters: {
            type: 'object',
            properties: {},
            required: []
          }
        }
      }
    };

    setSelectedTools([...selectedTools, newTool]);
    setCustomTool({});
    setShowCustomTool(false);
    setError(null);
  };

  const handleTestAgent = async () => {
    if (!testMessage.trim()) {
      setError('Please enter a test message');
      return;
    }

    setTesting(true);
    setError(null);
    setTestResponse('');

    try {
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/real-agent-tester`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            config: agentConfig,
            tools: selectedTools,
            message: testMessage,
            user_id: user?.id
          })
        }
      );

      if (!response.ok) {
        throw new Error(`Test failed: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.success) {
        setTestResponse(data.response);
      } else {
        throw new Error(data.error || 'Test failed');
      }
    } catch (error) {
      console.error('Test error:', error);
      setError(error instanceof Error ? error.message : 'Test failed');
    } finally {
      setTesting(false);
    }
  };

  const handleBuildAgent = async () => {
    if (!agentConfig.name || !agentConfig.description) {
      setError('Agent name and description are required');
      return;
    }

    setBuilding(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/real-agent-builder`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            config: agentConfig,
            tools: selectedTools,
            memory_config: memorySystemConfig,
            user_id: user?.id
          })
        }
      );

      if (!response.ok) {
        throw new Error(`Build failed: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.success) {
        setSuccess(`Agent "${agentConfig.name}" built successfully! Agent ID: ${data.agent_id}`);
        setStep('deploy');
      } else {
        throw new Error(data.error || 'Build failed');
      }
    } catch (error) {
      console.error('Build error:', error);
      setError(error instanceof Error ? error.message : 'Build failed');
    } finally {
      setBuilding(false);
    }
  };

  const renderConfigStep = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Agent Name</label>
          <input
            type="text"
            value={agentConfig.name}
            onChange={(e) => setAgentConfig(prev => ({ ...prev, name: e.target.value }))}
            placeholder="My Awesome Agent"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">LLM Provider</label>
          <select
            value={agentConfig.llmProvider}
            onChange={(e) => {
              const provider = LLM_PROVIDERS.find(p => p.id === e.target.value);
              setAgentConfig(prev => ({ 
                ...prev, 
                llmProvider: e.target.value,
                model: provider?.models[0] || ''
              }));
            }}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            {LLM_PROVIDERS.map(provider => (
              <option key={provider.id} value={provider.id}>{provider.name}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Model</label>
          <select
            value={agentConfig.model}
            onChange={(e) => setAgentConfig(prev => ({ ...prev, model: e.target.value }))}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            {LLM_PROVIDERS.find(p => p.id === agentConfig.llmProvider)?.models.map(model => (
              <option key={model} value={model}>{model}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Temperature</label>
          <input
            type="range"
            min="0"
            max="1"
            step="0.1"
            value={agentConfig.temperature}
            onChange={(e) => setAgentConfig(prev => ({ ...prev, temperature: parseFloat(e.target.value) }))}
            className="w-full"
          />
          <div className="text-sm text-gray-500 mt-1">Current: {agentConfig.temperature}</div>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
        <textarea
          value={agentConfig.description}
          onChange={(e) => setAgentConfig(prev => ({ ...prev, description: e.target.value }))}
          placeholder="Describe what your agent does and how it helps users..."
          rows={3}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Personality & Behavior</label>
        <textarea
          value={agentConfig.personality}
          onChange={(e) => setAgentConfig(prev => ({ ...prev, personality: e.target.value }))}
          placeholder="You are a helpful AI assistant that specializes in..."
          rows={3}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Custom Instructions</label>
        <textarea
          value={agentConfig.instructions}
          onChange={(e) => setAgentConfig(prev => ({ ...prev, instructions: e.target.value }))}
          placeholder="Additional instructions for how the agent should behave..."
          rows={4}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={agentConfig.a2aEnabled}
            onChange={(e) => setAgentConfig(prev => ({ ...prev, a2aEnabled: e.target.checked }))}
            className="rounded text-blue-600"
          />
          <span className="text-sm font-medium">Enable A2A Communication</span>
        </label>

        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={agentConfig.memoryEnabled}
            onChange={(e) => setAgentConfig(prev => ({ ...prev, memoryEnabled: e.target.checked }))}
            className="rounded text-blue-600"
          />
          <span className="text-sm font-medium">Enable Memory</span>
        </label>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Pricing</label>
          <select
            value={agentConfig.pricingModel}
            onChange={(e) => setAgentConfig(prev => ({ ...prev, pricingModel: e.target.value as any }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
          >
            <option value="free">Free</option>
            <option value="one-time">One-time Purchase</option>
            <option value="subscription_eligible">Subscription Eligible</option>
          </select>
          {agentConfig.pricingModel !== 'free' && (
            <input
              type="number"
              min="0"
              max="20"
              value={agentConfig.price}
              onChange={(e) => setAgentConfig(prev => ({ ...prev, price: parseFloat(e.target.value) || 0 }))}
              placeholder="Price ($)"
              className="w-full mt-2 px-3 py-2 border border-gray-300 rounded-lg text-sm"
            />
          )}
        </div>

        {/* Memory System Configuration */}
        <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
          <h4 className="font-medium text-purple-900 mb-3 flex items-center gap-2">
            <Brain className="w-5 h-5" />
            Memory System Configuration
          </h4>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-purple-900 mb-1">Memory Type</label>
              <select
                value={memorySystemConfig.type}
                onChange={(e) => setMemorySystemConfig(prev => ({ ...prev, type: e.target.value as any }))}
                className="w-full px-3 py-2 border border-purple-300 rounded-lg text-sm"
              >
                <option value="conversational">Conversational (Chat History)</option>
                <option value="factual">Factual (Key Facts)</option>
                <option value="episodic">Episodic (Experiences)</option>
                <option value="semantic">Semantic (Knowledge)</option>
                <option value="hybrid">Hybrid (All Types)</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-purple-900 mb-1">Retention Days</label>
              <input
                type="number"
                min="1"
                max="365"
                value={memorySystemConfig.retention_days}
                onChange={(e) => setMemorySystemConfig(prev => ({ ...prev, retention_days: parseInt(e.target.value) }))}
                className="w-full px-3 py-2 border border-purple-300 rounded-lg text-sm"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-purple-900 mb-1">Max Conversations</label>
              <input
                type="number"
                min="10"
                max="1000"
                value={memorySystemConfig.max_conversations}
                onChange={(e) => setMemorySystemConfig(prev => ({ ...prev, max_conversations: parseInt(e.target.value) }))}
                className="w-full px-3 py-2 border border-purple-300 rounded-lg text-sm"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-purple-900 mb-1">Learning Rate</label>
              <input
                type="range"
                min="0"
                max="1"
                step="0.1"
                value={memorySystemConfig.learning_rate}
                onChange={(e) => setMemorySystemConfig(prev => ({ ...prev, learning_rate: parseFloat(e.target.value) }))}
                className="w-full"
              />
              <div className="text-xs text-purple-700 mt-1">Current: {memorySystemConfig.learning_rate}</div>
            </div>
          </div>

          <div className="mt-4 flex items-center">
            <input
              type="checkbox"
              checked={memorySystemConfig.auto_cleanup}
              onChange={(e) => setMemorySystemConfig(prev => ({ ...prev, auto_cleanup: e.target.checked }))}
              className="rounded text-purple-600"
              id="auto-cleanup"
            />
            <label htmlFor="auto-cleanup" className="ml-2 text-sm text-purple-800">
              Enable automatic cleanup of old memories
            </label>
          </div>
        </div>
      </div>
    </div>
  );

  const renderToolsStep = () => (
    <div className="space-y-6">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h3 className="font-medium text-blue-900 mb-2">Tools & Functions</h3>
        <p className="text-blue-700 text-sm">
          Tools allow your agent to interact with external services, perform calculations, and access real-time data.
        </p>
      </div>

      {/* Selected Tools */}
      {selectedTools.length > 0 && (
        <div>
          <h4 className="font-medium text-gray-900 mb-3">Selected Tools ({selectedTools.length})</h4>
          <div className="space-y-2">
            {selectedTools.map(tool => (
              <div key={tool.id} className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-center gap-3">
                  <Tool className="w-5 h-5 text-green-600" />
                  <div>
                    <div className="font-medium text-green-900">{tool.name}</div>
                    <div className="text-sm text-green-700">{tool.description}</div>
                  </div>
                </div>
                <button
                  onClick={() => handleRemoveTool(tool.id)}
                  className="p-1 text-red-600 hover:bg-red-100 rounded-full"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Available Tools */}
      <div>
        <h4 className="font-medium text-gray-900 mb-3">Available Tools</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {PREDEFINED_TOOLS.filter(tool => !selectedTools.find(t => t.id === tool.id)).map(tool => (
            <div key={tool.id} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors">
              <div className="flex items-center justify-between mb-2">
                <h5 className="font-medium text-gray-900">{tool.name}</h5>
                <button
                  onClick={() => handleAddTool(tool)}
                  className="p-1 bg-blue-600 text-white rounded-full hover:bg-blue-700"
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>
              <p className="text-sm text-gray-600">{tool.description}</p>
              <div className="mt-2 text-xs text-gray-500">Type: {tool.type}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Custom Tool */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-medium text-gray-900">Custom Tool</h4>
          <button
            onClick={() => setShowCustomTool(!showCustomTool)}
            className="px-3 py-1 bg-purple-600 text-white rounded-lg hover:bg-purple-700 text-sm"
          >
            {showCustomTool ? 'Cancel' : 'Add Custom Tool'}
          </button>
        </div>

        {showCustomTool && (
          <div className="border border-gray-200 rounded-lg p-4 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tool Name</label>
                <input
                  type="text"
                  value={customTool.name || ''}
                  onChange={(e) => setCustomTool(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="My Custom Tool"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tool Type</label>
                <select
                  value={customTool.type || 'function'}
                  onChange={(e) => setCustomTool(prev => ({ ...prev, type: e.target.value as any }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                >
                  <option value="function">Function</option>
                  <option value="api">API Call</option>
                  <option value="database">Database Query</option>
                  <option value="web_search">Web Search</option>
                </select>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <textarea
                value={customTool.description || ''}
                onChange={(e) => setCustomTool(prev => ({ ...prev, description: e.target.value }))}
                placeholder="What does this tool do?"
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              />
            </div>
            <div className="flex justify-end">
              <button
                onClick={handleAddCustomTool}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
              >
                Add Tool
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  const renderTestStep = () => (
    <div className="space-y-6">
      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
        <h3 className="font-medium text-green-900 mb-2">Test Your Agent</h3>
        <p className="text-green-700 text-sm">
          Test your agent's responses and tool usage before deploying it.
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Test Message</label>
        <textarea
          value={testMessage}
          onChange={(e) => setTestMessage(e.target.value)}
          placeholder="Hello! What can you help me with?"
          rows={3}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
      </div>

      <button
        onClick={handleTestAgent}
        disabled={testing || !testMessage.trim()}
        className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
      >
        {testing ? (
          <>
            <Loader className="w-5 h-5 animate-spin" />
            Testing Agent...
          </>
        ) : (
          <>
            <Play className="w-5 h-5" />
            Test Agent
          </>
        )}
      </button>

      {testResponse && (
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <h4 className="font-medium text-gray-900 mb-2">Agent Response:</h4>
          <div className="bg-gray-50 p-3 rounded-lg">
            <pre className="text-sm text-gray-700 whitespace-pre-wrap">{testResponse}</pre>
          </div>
        </div>
      )}

      {/* Agent Summary */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
        <h4 className="font-medium text-gray-900 mb-3">Agent Summary</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div>
            <span className="font-medium text-gray-700">Name:</span>
            <span className="ml-2 text-gray-600">{agentConfig.name}</span>
          </div>
          <div>
            <span className="font-medium text-gray-700">LLM:</span>
            <span className="ml-2 text-gray-600">{agentConfig.llmProvider} ({agentConfig.model})</span>
          </div>
          <div>
            <span className="font-medium text-gray-700">Tools:</span>
            <span className="ml-2 text-gray-600">{selectedTools.length} tools</span>
          </div>
          <div>
            <span className="font-medium text-gray-700">A2A:</span>
            <span className="ml-2 text-gray-600">{agentConfig.a2aEnabled ? 'Enabled' : 'Disabled'}</span>
          </div>
          <div>
            <span className="font-medium text-gray-700">Memory:</span>
            <span className="ml-2 text-gray-600">{agentConfig.memoryEnabled ? 'Enabled' : 'Disabled'}</span>
          </div>
          <div>
            <span className="font-medium text-gray-700">Price:</span>
            <span className="ml-2 text-gray-600">
              {agentConfig.pricingModel === 'free' ? 'Free' : `$${agentConfig.price}`}
            </span>
          </div>
        </div>
      </div>
    </div>
  );

  const renderDeployStep = () => (
    <div className="space-y-6">
      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
        <div className="flex items-center gap-2 text-green-700 mb-2">
          <CheckCircle className="w-5 h-5" />
          <span className="font-medium">Agent Built Successfully!</span>
        </div>
        <p className="text-green-600 text-sm">
          Your agent is ready to deploy and start helping users.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <button className="flex flex-col items-center gap-3 p-6 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
          <Globe className="w-8 h-8 text-blue-600" />
          <div className="text-center">
            <div className="font-medium text-gray-900">Deploy to Web</div>
            <div className="text-sm text-gray-600">Make your agent accessible via web interface</div>
          </div>
        </button>

        <button className="flex flex-col items-center gap-3 p-6 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
          <Code className="w-8 h-8 text-purple-600" />
          <div className="text-center">
            <div className="font-medium text-gray-900">Get API Access</div>
            <div className="text-sm text-gray-600">Integrate your agent via API calls</div>
          </div>
        </button>
      </div>

      <button
        onClick={onClose}
        className="w-full px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 transition-colors"
      >
        Done
      </button>
    </div>
  );

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gradient-to-r from-blue-600 to-purple-600 text-white">
          <div className="flex items-center gap-3">
            <Bot className="w-6 h-6" />
            <div>
              <h2 className="text-xl font-semibold">Real Agent Builder</h2>
              <p className="text-sm opacity-90">Build powerful AI agents with tools and memory</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/20 rounded-lg transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Progress Steps */}
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            {[
              { id: 'config', label: 'Configure', icon: Settings },
              { id: 'tools', label: 'Tools', icon: Tool },
              { id: 'test', label: 'Test', icon: Play },
              { id: 'deploy', label: 'Deploy', icon: Zap }
            ].map((stepItem, index) => (
              <div key={stepItem.id} className="flex items-center">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                  step === stepItem.id
                    ? 'bg-blue-600 text-white'
                    : index < ['config', 'tools', 'test', 'deploy'].indexOf(step)
                    ? 'bg-green-600 text-white'
                    : 'bg-gray-200 text-gray-600'
                }`}>
                  <stepItem.icon className="w-4 h-4" />
                </div>
                <span className={`ml-2 text-sm font-medium ${
                  step === stepItem.id
                    ? 'text-blue-600'
                    : index < ['config', 'tools', 'test', 'deploy'].indexOf(step)
                    ? 'text-green-600'
                    : 'text-gray-500'
                }`}>
                  {stepItem.label}
                </span>
                {index < 3 && (
                  <div className={`w-16 h-0.5 mx-4 ${
                    index < ['config', 'tools', 'test', 'deploy'].indexOf(step)
                      ? 'bg-green-600'
                      : 'bg-gray-200'
                  }`} />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {success && (
            <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2 text-green-700">
              <CheckCircle className="w-5 h-5 flex-shrink-0" />
              <span>{success}</span>
            </div>
          )}

          {step === 'config' && renderConfigStep()}
          {step === 'tools' && renderToolsStep()}
          {step === 'test' && renderTestStep()}
          {step === 'deploy' && renderDeployStep()}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 p-6">
          <div className="flex justify-between">
            <button
              onClick={() => {
                const steps = ['config', 'tools', 'test', 'deploy'];
                const currentIndex = steps.indexOf(step);
                if (currentIndex > 0) {
                  setStep(steps[currentIndex - 1] as any);
                }
              }}
              disabled={step === 'config'}
              className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Previous
            </button>

            {step !== 'deploy' ? (
              <button
                onClick={() => {
                  if (step === 'test') {
                    handleBuildAgent();
                  } else {
                    const steps = ['config', 'tools', 'test', 'deploy'];
                    const currentIndex = steps.indexOf(step);
                    if (currentIndex < steps.length - 1) {
                      setStep(steps[currentIndex + 1] as any);
                    }
                  }
                }}
                disabled={building || (step === 'config' && (!agentConfig.name || !agentConfig.description))}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
              >
                {building ? (
                  <>
                    <Loader className="w-4 h-4 animate-spin" />
                    Building...
                  </>
                ) : step === 'test' ? (
                  <>
                    <Save className="w-4 h-4" />
                    Build Agent
                  </>
                ) : (
                  'Next'
                )}
              </button>
            ) : (
              <button
                onClick={onClose}
                className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                Close
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}